import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/hooks/useAuth';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireRole } from '@/components/RequireRole';
import { AppShell } from '@/components/layout/AppShell';
import { Login } from '@/pages/Login';
import { LiveConsole } from '@/pages/LiveConsole';
import { Holdings } from '@/pages/Holdings';
import { Watchlist } from '@/pages/Watchlist';
import { AgentChains } from '@/pages/AgentChains';
import { Scanners } from '@/pages/Scanners';
import { Cost } from '@/pages/Cost';
import { RiskPosture } from '@/pages/RiskPosture';
import { TradeJournal } from '@/pages/TradeJournal';
import { News } from '@/pages/News';
import { SystemHealth } from '@/pages/SystemHealth';
import { Reflections } from '@/pages/Reflections';
import { More } from '@/pages/More';

// Performance + Analytics both pull in ECharts (~250kB). Lazy-load so users
// who never visit those pages don't pay for it on initial load. They share
// the chunk thanks to Vite's automatic vendor splitting.
const Performance = lazy(() =>
  import('@/pages/Performance').then((m) => ({ default: m.Performance })),
);
const Analytics = lazy(() =>
  import('@/pages/Analytics').then((m) => ({ default: m.Analytics })),
);

const Loader = () => (
  <div className="p-8 text-text-faint text-sm">Loading…</div>
);

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />

        {/* Protected — RequireAuth wraps AppShell, RequireRole gates OPERATE pages.
            Backend Spring Security enforces 403 independently; this is UX only. */}
        <Route element={<RequireAuth />}>
          <Route path="/" element={<AppShell />}>
            <Route element={<RequireRole />}>
              <Route index element={<Navigate to="/live" replace />} />
              <Route path="live" element={<LiveConsole />} />
              <Route path="holdings" element={<Holdings />} />
              <Route path="watchlist" element={<Watchlist />} />
              <Route path="agents" element={<AgentChains />} />
              <Route path="performance" element={<Suspense fallback={<Loader />}><Performance /></Suspense>} />
              <Route path="analytics" element={<Suspense fallback={<Loader />}><Analytics /></Suspense>} />
              <Route path="trades" element={<TradeJournal />} />
              <Route path="reflections" element={<Reflections />} />
              <Route path="scanners" element={<Scanners />} />
              <Route path="cost" element={<Cost />} />
              <Route path="risk" element={<RiskPosture />} />
              <Route path="news" element={<News />} />
              <Route path="system" element={<SystemHealth />} />
              <Route path="more" element={<More />} />
              <Route path="*" element={<Navigate to="/live" replace />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}
